/**
 * MBA Lab — shared library for every /tools page (roi.js, team-charter.js, ...).
 * Nothing here touches the DOM beyond download()'s anchor click - formatting,
 * storage, and analytics only.
 */

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1
});

const numberFormatter = new Intl.NumberFormat('en-US');

export const fmt = {
  /** @param {number} value - dollars. */
  currency(value) {
    return currencyFormatter.format(Number.isFinite(value) ? value : 0);
  },
  /** @param {number} value - a fraction, e.g. 0.08 -> "8.0%". */
  percent(value) {
    return percentFormatter.format(Number.isFinite(value) ? value : 0);
  },
  /** @param {number} value */
  number(value) {
    return numberFormatter.format(Number.isFinite(value) ? value : 0);
  }
};

/**
 * The signed-in caller's opaque storage namespace, or null when nobody is signed in. Emitted by
 * Views/Shared/_AppConfig.cshtml, which only renders it on an authenticated response.
 *
 * It is a derived value rather than the user id itself: nothing on the page needs to know who you
 * are to keep your work apart from the next person's, and a raw id in a storage key is a durable
 * copy of your identity left on a machine that may not be yours.
 */
function accountNamespace() {
  try {
    const ns = window.__config && window.__config.toolNs;
    return typeof ns === 'string' && ns.length > 0 ? ns : null;
  } catch {
    return null;
  }
}

/**
 * ANONYMOUS work and ACCOUNT work never share a key, and that separation is the whole reason this
 * function takes a namespace at all.
 *
 * Shared and family computers are the normal case for students. If a signed-in person's synced
 * state were written to the plain key, then signing out - or someone else signing in - would leave
 * their charter, their shortlist, their draft sitting there for whoever opened the tool next. The
 * mirror image is just as bad: the stranger's leftover draft would upload into whichever account
 * signed in after them.
 *
 * This covers the tiered tools' `{slug}-remember` preference too, and that is deliberate rather
 * than incidental: that flag is a consent decision ("keep this past today"), and consent must not
 * carry over from an anonymous session into an account, or between two people sharing a browser.
 * The visible consequence is that someone who had it on before signing in sees it off afterwards -
 * which is the fail-closed direction, and the only safe one for a switch that governs whether a
 * drafted conversation about a named person is written down at all.
 *
 * @param {string} tool - the registry slug.
 * @param {string|null} namespace - null for anonymous, which keeps the historical key exactly.
 */
function storageKey(tool, namespace) {
  return namespace ? `mbalab:account:${namespace}:tools:${tool}:v1` : `mbalab:tools:${tool}:v1`;
}

/**
 * Private-mode Safari (and any other storage failure - quota, disabled
 * storage) degrades to a no-op instead of throwing: a tool must keep working
 * with persistence simply disabled.
 */
function storageWrapper(storage, key) {
  return {
    load() {
      try {
        const raw = storage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    save(value) {
      try {
        storage.setItem(key, JSON.stringify(value));
        return true;
      } catch {
        return false;
      }
    },
    clear() {
      try {
        storage.removeItem(key);
      } catch {
        // Nothing to clean up if storage was never writable.
      }
    }
  };
}

/**
 * localStorage wrapper scoped to one tool - persists across browser
 * sessions until explicitly cleared.
 * @param {string} tool - matches the tool's registry slug, e.g. "team-charter-builder".
 */
export function store(tool) {
  return storageWrapper(window.localStorage, storageKey(tool, accountNamespace()));
}

/**
 * The same tool's ANONYMOUS localStorage, read explicitly while signed in. Exists for exactly one
 * job: offering to bring a draft someone started before signing in into their account (see
 * account-sync.js's import prompt). Never written through - the anonymous draft stays whoever's it
 * was, and is left alone if the offer is declined.
 * @param {string} tool
 */
export function anonymousStore(tool) {
  return storageWrapper(window.localStorage, storageKey(tool, null));
}

/** True when this page was rendered for a signed-in visitor, i.e. storage is account-scoped. */
export function hasAccountNamespace() {
  return accountNamespace() !== null;
}

/**
 * Same shape as store(), backed by sessionStorage instead - cleared when the
 * tab/browser session ends. Used where a tool's default persistence is
 * deliberately ephemeral rather than indefinite (see accountability-
 * planner.js's session-by-default, opt-in-persistent split).
 * @param {string} tool
 */
export function sessionStore(tool) {
  return storageWrapper(window.sessionStorage, storageKey(tool, accountNamespace()));
}

/**
 * Triggers a client-side file download with no server round-trip.
 * @param {string} filename
 * @param {string} mime
 * @param {string} text
 */
export function download(filename, mime, text) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Fires a `tool_<action>` GA4 event via the sitewide analytics helper (see
 * src/analytics.js). `action` is passed WITHOUT a "tool_" prefix - this function
 * adds it once, so callers can never produce a doubled "tool_tool_open" name.
 * Soft no-ops (like the rest of MbaLabAnalytics) when gtag/GA isn't configured.
 * @param {string} toolName - the tool's registry slug, e.g. "mba-roi-calculator".
 * @param {string} action - "open" | "engage" | "result" | "download" | "copy" |
 *   "print" | "crosslink_click".
 * @param {Object} [params] - coarse, non-identifying params only. Never send
 *   exact salary/tuition/debt/scholarship figures or free-text values.
 */
export function trackToolAction(toolName, action, params) {
  if (typeof window === 'undefined' || !window.MbaLabAnalytics) return;
  window.MbaLabAnalytics.trackEvent(`tool_${action}`, { tool_name: toolName, ...(params || {}) });
}
